/**
 * Mode-style cards for SpeedRun / Golf level pickers.
 */

export type LevelCardKind = "speedrun" | "penalty";

export type LevelCardData = {
    levelIndex: number;
    title: string;
    description?: string | null;
    authorName?: string | null;
    viewCount?: number;
};

export type BuildLevelPickerGridOpts = {
    kind: LevelCardKind;
    levels: LevelCardData[];
    selectedLevel?: number | null;
    onSelect: (levelIndex: number) => void;
    emptyMessage?: string;
};

function formatViewCount(n: number): string {
    return Math.max(0, Math.floor(n)).toLocaleString("en-US");
}

function defaultTitle(kind: LevelCardKind, levelIndex: number): string {
    return kind === "penalty" ? `Golf ${levelIndex}` : `Level ${levelIndex}`;
}

export function buildLevelPickerGrid(mount: HTMLElement, opts: BuildLevelPickerGridOpts): void {
    mount.replaceChildren();
    mount.classList.add("levelPickerGrid");

    if (!opts.levels.length) {
        const p = document.createElement("p");
        p.className = "accessHint";
        p.textContent = opts.emptyMessage || "No levels available.";
        mount.appendChild(p);
        return;
    }

    for (const row of opts.levels) {
        const card = document.createElement("div");
        card.className = "modeBox levelPickerCard";
        card.dataset.levelIndex = String(row.levelIndex);
        card.setAttribute("role", "button");
        card.tabIndex = 0;

        const selected = opts.selectedLevel != null && row.levelIndex === opts.selectedLevel;
        if (selected) card.classList.add("levelPickerCard--selected");

        const title = document.createElement("h3");
        title.textContent = row.title.trim() || defaultTitle(opts.kind, row.levelIndex);
        card.appendChild(title);

        const descText = (row.description || "").trim();
        if (descText) {
            const desc = document.createElement("p");
            desc.className = "levelPickerCardDesc";
            desc.textContent = descText;
            card.appendChild(desc);
        }

        const author = document.createElement("span");
        author.className = "levelPickerCardAuthor";
        const authorLabel = (row.authorName || "").trim() || "Unknown";
        author.textContent = `Made by ${authorLabel}`;
        card.appendChild(author);

        const views = document.createElement("span");
        views.className = "levelPickerCardViews";
        const count = formatViewCount(row.viewCount ?? 0);
        views.textContent = count;
        views.title = "Level views";
        views.setAttribute("aria-label", `${count} views`);
        card.appendChild(views);

        const pick = () => opts.onSelect(row.levelIndex);
        card.addEventListener("click", pick);
        card.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                pick();
            }
        });

        mount.appendChild(card);
    }
}

export function markLevelPickerSelection(mount: HTMLElement, selectedLevel: number | null): void {
    mount.querySelectorAll<HTMLElement>(".levelPickerCard").forEach((card) => {
        const idx = Math.floor(Number(card.dataset.levelIndex) || 0);
        const picked = selectedLevel != null && idx === selectedLevel;
        card.classList.toggle("levelPickerCard--selected", picked);
    });
}
